import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import getProductDetails from "./getProductDetails";
import editProductDetails from "./editProductDetails";
import addProductImage from "./addProductImage";
import deleteProductImage from "./deleteProductImage";
import uploadProductDocument from "./uploadProductDocument";
import deleteProductDocument from "./deleteProductDocument";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faTag,
  faSyncAlt,
  faPlus,
  faSearch,
  faPen,
  faTrash,
  faTimes,
  faSave,
  faChevronLeft,
  faChevronRight,
  faEye,
  faBoxOpen,
  faImage,
  faFilePdf,
  faFlask,
  faLeaf,
  faInfoCircle,
  faTh,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import ModalAlert from "../reusable/ModalAlert";
import ConfirmBox from "../reusable/ConfirmBox";

/* ─── Palette ─────────────────────────────────────────────────────────────── */
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#f4f6f5",
  border: "#d8e8dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  red: "#c62828",
  redBg: "#fdecea",
};

/* ─── Product Detail Drawer ──────────────────────────────────────────────── */
const EMPTY_DETAILS = {
  subtitle: "",
  shortDescription: "",
  specifications: [],
  advisoryDosage: [],
  packing: [],
};

export function ProductDetailDrawer({ open, product, onClose, onSaved }) {
  const [mode, setMode] = useState("view");
  const [loading, setLoading] = useState(false);
  const [baseDetails, setBaseDetails] = useState(null);
  const [draft, setDraft] = useState({});
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const [alertVariant, setAlertVariant] = useState("success");
  const imgInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    if (open && product) {
      setMode("view");
      setLoading(true);
      getProductDetails(product._id).then((res) => {
        setLoading(false);
        if (res?.data) {
          const d = res.data;
          const details = {
            subtitle: d.subtitle || "",
            shortDescription: d.shortDescription || "",
            specifications: d.specifications || [],
            advisoryDosage: d.advisoryDosage || [],
            packing: d.packing || [],
          };
          setBaseDetails(details);
          setDraft({ name: d.product || "", ...details });
          setImages(d.images || []);
          setDocuments(d.documents || []);
        } else {
          setBaseDetails({ ...EMPTY_DETAILS });
          setDraft({ name: product.product || "", ...EMPTY_DETAILS });
          setImages([]);
          setDocuments([]);
        }
      });
    }
  }, [open, product]);

  if (!open) return null;
  if (loading) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1050,
            backgroundColor: "rgba(0,0,0,0.42)",
          }}
        />
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: 760,
            maxWidth: "96vw",
            zIndex: 1060,
            backgroundColor: C.page,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: C.mid, fontWeight: 600 }}>Loading…</span>
        </div>
      </>
    );
  }
  if (!baseDetails) return null;

  const D =
    mode === "view" ? { name: product?.product, ...baseDetails } : draft;

  const setF = (field, val) => setDraft((p) => ({ ...p, [field]: val }));
  const setSpec = (i, k, v) => {
    const arr = [...draft.specifications];
    arr[i] = { ...arr[i], [k]: v };
    setF("specifications", arr);
  };
  const setDose = (i, k, v) => {
    const arr = [...draft.advisoryDosage];
    arr[i] = { ...arr[i], [k]: v };
    setF("advisoryDosage", arr);
  };

  const handleSave = () => {
    setSaving(true);
    editProductDetails({
      _id: product._id,
      product: draft.name,
      subtitle: draft.subtitle,
      shortDescription: draft.shortDescription,
      specifications: draft.specifications,
      advisoryDosage: draft.advisoryDosage,
      packing: draft.packing,
    }).then((res) => {
      setSaving(false);
      if (res?.data) {
        const details = {
          subtitle: draft.subtitle,
          shortDescription: draft.shortDescription,
          specifications: draft.specifications,
          advisoryDosage: draft.advisoryDosage,
          packing: draft.packing,
        };
        setBaseDetails(details);
        setMode("view");
        setAlertVariant("success");
        setAlertMsg("Product saved successfully.");
        if (onSaved) onSaved(res.data);
      } else {
        setAlertVariant("danger");
        setAlertMsg("Save failed. Please try again.");
      }
    });
  };

  const handleImageClick = (slot) => {
    if (mode === "edit") {
      if (images[slot]) return;
      imgInputRef.current._slot = slot;
      imgInputRef.current.click();
    } else if (images[slot]) {
      setLightboxUrl(images[slot].imageUrl);
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    addProductImage(product._id, file).then((res) => {
      setUploadingImg(false);
      if (res?.data?._id) {
        setImages((prev) => [...prev, res.data]);
      } else {
        setAlertVariant("danger");
        setAlertMsg("Image upload failed.");
      }
    });
    e.target.value = "";
  };

  const handleDeleteImage = (imgId) => {
    deleteProductImage(imgId).then((res) => {
      setImages((prev) => prev.filter((img) => img._id !== imgId));
    });
  };

  const handleDocClick = () => {
    if (mode !== "edit") return;
    docInputRef.current.click();
  };

  const handleDocFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDoc(true);
    uploadProductDocument(product._id, file, "article_pdf").then((res) => {
      setUploadingDoc(false);
      if (res?.data?._id) {
        setDocuments([res.data]);
      } else {
        setAlertVariant("danger");
        setAlertMsg("Document upload failed.");
      }
    });
    e.target.value = "";
  };

  const handleDeleteDoc = (docId) => {
    deleteProductDocument(docId).then(() => {
      setDocuments([]);
    });
  };

  const articlePdf = documents.find((d) => d.docType === "article_pdf") || null;

  const S = {
    sectionTitle: {
      fontSize: "0.72rem",
      fontWeight: 700,
      color: C.mid,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    card: {
      backgroundColor: "#fff",
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      marginBottom: "1rem",
      overflow: "hidden",
    },
    cardBody: { padding: "1rem 1.1rem" },
    inp: {
      width: "100%",
      borderRadius: 7,
      border: `1.5px solid ${C.border}`,
      padding: "0.42rem 0.65rem",
      fontSize: "0.85rem",
      color: C.textDk,
      outline: "none",
      boxSizing: "border-box",
      fontFamily: "inherit",
    },
    ta: {
      width: "100%",
      borderRadius: 7,
      border: `1.5px solid ${C.border}`,
      padding: "0.55rem 0.7rem",
      fontSize: "0.85rem",
      color: C.textDk,
      outline: "none",
      resize: "vertical",
      minHeight: 80,
      boxSizing: "border-box",
      fontFamily: "inherit",
    },
    lbl: {
      fontSize: "0.7rem",
      fontWeight: 600,
      color: C.textGr,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 4,
      display: "block",
    },
    th: {
      backgroundColor: "#f0f6f3",
      fontSize: "0.72rem",
      fontWeight: 700,
      color: C.mid,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "0.5rem 0.75rem",
      textAlign: "left",
    },
    td: {
      padding: "0.6rem 0.75rem",
      fontSize: "0.84rem",
      color: C.textDk,
      borderBottom: `1px solid ${C.border}`,
    },
    addRowBtn: {
      height: 26,
      padding: "0 10px",
      borderRadius: 6,
      border: `1.5px solid ${C.border}`,
      backgroundColor: C.white,
      color: C.mid,
      fontSize: "0.72rem",
      fontWeight: 600,
      cursor: "pointer",
    },
    delBtn: {
      width: 28,
      height: 28,
      borderRadius: 6,
      border: "none",
      backgroundColor: C.redBg,
      color: C.red,
      cursor: "pointer",
      flexShrink: 0,
      fontSize: "0.65rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1050,
          backgroundColor: "rgba(0,0,0,0.42)",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 760,
          maxWidth: "96vw",
          zIndex: 1060,
          backgroundColor: C.page,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-6px 0 32px rgba(0,0,0,0.22)",
          animation: "slideIn 0.28s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "1rem 1.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                backgroundColor: C.mid,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faBoxOpen}
                style={{ color: "#fff", fontSize: "0.9rem" }}
              />
            </div>
            <div>
              {mode === "view" ? (
                <>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.02rem",
                      color: "#fff",
                    }}
                  >
                    {D.name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "rgba(255,255,255,0.58)",
                      marginTop: 2,
                    }}
                  >
                    {D.subtitle}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#fff",
                  }}
                >
                  Edit Product
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mode === "view" ? (
              <button
                onClick={() => setMode("edit")}
                style={{
                  height: 34,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: "1.5px solid rgba(255,255,255,0.28)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.68rem" }} />{" "}
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    height: 34,
                    padding: "0 14px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: saving ? C.mid : C.bright,
                    color: "#fff",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: saving ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: saving ? 0.75 : 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faSave}
                    style={{ fontSize: "0.68rem" }}
                  />{" "}
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => {
                    setDraft({ name: product?.product || "", ...baseDetails });
                    setMode("view");
                  }}
                  style={{
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1.5px solid rgba(255,255,255,0.28)",
                    backgroundColor: "transparent",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                width: 32,
                height: 32,
                borderRadius: 7,
                background: "rgba(255,255,255,0.12)",
                border: "none",
                cursor: "pointer",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.85rem" }} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
          {/* Name & Subtitle — edit mode only */}
          {mode === "edit" && (
            <div style={S.card}>
              <div style={S.cardBody}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.85rem",
                  }}
                >
                  <div>
                    <label style={S.lbl}>Product Name</label>
                    <input
                      style={S.inp}
                      value={draft.name}
                      onChange={(e) => setF("name", e.target.value)}
                      placeholder="Product name"
                    />
                  </div>
                  <div>
                    <label style={S.lbl}>Subtitle</label>
                    <input
                      style={S.inp}
                      value={draft.subtitle}
                      onChange={(e) => setF("subtitle", e.target.value)}
                      placeholder="Short tagline"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Product Images */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon
                  icon={faImage}
                  style={{ fontSize: "0.72rem" }}
                />{" "}
                Product Images
              </span>
            </div>
            <div
              style={{
                padding: "1rem 1.1rem",
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <input
                ref={imgInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageFileChange}
              />
              {[0, 1, 2].map((i) => {
                const img = images[i];
                return (
                  <div
                    key={i}
                    onClick={() => handleImageClick(i)}
                    style={{
                      width: 140,
                      height: 105,
                      borderRadius: 9,
                      backgroundColor: C.light,
                      border: `1.5px dashed ${C.border}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      cursor: mode === "edit" && !img ? "pointer" : (img && mode !== "edit" ? "zoom-in" : "default"),
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {img ? (
                      <>
                        <img
                          src={img.imageUrl}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                        {mode === "edit" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(img._id);
                            }}
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              border: "none",
                              backgroundColor: C.red,
                              color: "#fff",
                              cursor: "pointer",
                              fontSize: "0.6rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faImage}
                          style={{ fontSize: "1.6rem", color: C.border }}
                        />
                        {mode === "edit" && (
                          <span
                            style={{
                              fontSize: "0.67rem",
                              color: C.textGr,
                              fontWeight: 500,
                            }}
                          >
                            {uploadingImg && images.length === i
                              ? "Uploading…"
                              : "Click to upload"}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Short Description */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ fontSize: "0.72rem" }}
                />{" "}
                Short Description
              </span>
            </div>
            <div style={S.cardBody}>
              {mode === "view" ? (
                <p
                  style={{
                    fontSize: "0.87rem",
                    color: C.textDk,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {D.shortDescription}
                </p>
              ) : (
                <textarea
                  style={S.ta}
                  value={draft.shortDescription}
                  onChange={(e) => setF("shortDescription", e.target.value)}
                  rows={4}
                />
              )}
            </div>
          </div>

          {/* Specifications */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon
                  icon={faFlask}
                  style={{ fontSize: "0.72rem" }}
                />{" "}
                Specifications
              </span>
              {mode === "edit" && (
                <button
                  style={S.addRowBtn}
                  onClick={() =>
                    setF("specifications", [
                      ...draft.specifications,
                      { key: "", value: "" },
                    ])
                  }
                >
                  + Add Row
                </button>
              )}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "36%" }}>Parameter</th>
                  <th style={S.th}>Value</th>
                </tr>
              </thead>
              <tbody>
                {(mode === "view"
                  ? D.specifications
                  : draft.specifications
                ).map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: i % 2 === 0 ? "#fff" : "#f8fbf9",
                    }}
                  >
                    <td style={S.td}>
                      {mode === "view" ? (
                        <span style={{ fontWeight: 600 }}>{row.key}</span>
                      ) : (
                        <input
                          style={{ ...S.inp, fontSize: "0.8rem" }}
                          value={row.key}
                          onChange={(e) => setSpec(i, "key", e.target.value)}
                          placeholder="Parameter"
                        />
                      )}
                    </td>
                    <td style={S.td}>
                      {mode === "view" ? (
                        row.value
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <input
                            style={{ ...S.inp, fontSize: "0.8rem", flex: 1 }}
                            value={row.value}
                            onChange={(e) =>
                              setSpec(i, "value", e.target.value)
                            }
                            placeholder="Value"
                          />
                          <button
                            style={S.delBtn}
                            onClick={() =>
                              setF(
                                "specifications",
                                draft.specifications.filter((_, j) => j !== i),
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Advisory Dosage */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon
                  icon={faLeaf}
                  style={{ fontSize: "0.72rem" }}
                />{" "}
                Advisory Dosage
              </span>
              {mode === "edit" && (
                <button
                  style={S.addRowBtn}
                  onClick={() =>
                    setF("advisoryDosage", [
                      ...draft.advisoryDosage,
                      { crop: "", dosage: "", frequency: "" },
                    ])
                  }
                >
                  + Add Row
                </button>
              )}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "28%" }}>Crop</th>
                  <th style={{ ...S.th, width: "28%" }}>Dosage</th>
                  <th style={S.th}>Frequency / Timing</th>
                </tr>
              </thead>
              <tbody>
                {(mode === "view"
                  ? D.advisoryDosage
                  : draft.advisoryDosage
                ).map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: i % 2 === 0 ? "#fff" : "#f8fbf9",
                    }}
                  >
                    <td style={S.td}>
                      {mode === "view" ? (
                        <span style={{ fontWeight: 500 }}>{row.crop}</span>
                      ) : (
                        <input
                          style={{ ...S.inp, fontSize: "0.78rem" }}
                          value={row.crop}
                          onChange={(e) => setDose(i, "crop", e.target.value)}
                          placeholder="Crop name"
                        />
                      )}
                    </td>
                    <td style={S.td}>
                      {mode === "view" ? (
                        row.dosage
                      ) : (
                        <input
                          style={{ ...S.inp, fontSize: "0.78rem" }}
                          value={row.dosage}
                          onChange={(e) => setDose(i, "dosage", e.target.value)}
                          placeholder="e.g. 1 ml/litre"
                        />
                      )}
                    </td>
                    <td style={S.td}>
                      {mode === "view" ? (
                        row.frequency
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <input
                            style={{ ...S.inp, fontSize: "0.78rem", flex: 1 }}
                            value={row.frequency}
                            onChange={(e) =>
                              setDose(i, "frequency", e.target.value)
                            }
                            placeholder="Timing / interval"
                          />
                          <button
                            style={S.delBtn}
                            onClick={() =>
                              setF(
                                "advisoryDosage",
                                draft.advisoryDosage.filter((_, j) => j !== i),
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Packing Options */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon icon={faBox} style={{ fontSize: "0.72rem" }} />{" "}
                Packing Options
              </span>
            </div>
            <div style={S.cardBody}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(mode === "view" ? D.packing : draft.packing).map((p, i) => (
                  <span
                    key={i}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: 30,
                      padding: "0 12px",
                      borderRadius: 20,
                      backgroundColor: C.light,
                      color: C.mid,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    {mode === "edit" ? (
                      <>
                        <input
                          value={p}
                          onChange={(e) => {
                            const a = [...draft.packing];
                            a[i] = e.target.value;
                            setF("packing", a);
                          }}
                          style={{
                            width: 70,
                            border: "none",
                            background: "transparent",
                            fontSize: "0.8rem",
                            color: C.mid,
                            fontWeight: 600,
                            outline: "none",
                          }}
                        />
                        <button
                          onClick={() =>
                            setF(
                              "packing",
                              draft.packing.filter((_, j) => j !== i),
                            )
                          }
                          style={{
                            marginLeft: 4,
                            width: 15,
                            height: 15,
                            borderRadius: "50%",
                            border: "none",
                            backgroundColor: C.red,
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: "0.55rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      p
                    )}
                  </span>
                ))}
                {mode === "edit" && (
                  <button
                    onClick={() => setF("packing", [...draft.packing, ""])}
                    style={{
                      height: 30,
                      padding: "0 12px",
                      borderRadius: 20,
                      border: `1.5px dashed ${C.border}`,
                      backgroundColor: "#fff",
                      color: C.textGr,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    + Add
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div style={S.card}>
            <div
              style={{
                padding: "0.7rem 1.1rem",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span style={S.sectionTitle}>
                <FontAwesomeIcon
                  icon={faFilePdf}
                  style={{ fontSize: "0.72rem" }}
                />{" "}
                Documents
              </span>
            </div>
            <div
              style={{
                padding: "1rem 1.1rem",
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "0.75rem",
              }}
            >
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf"
                style={{ display: "none" }}
                onChange={handleDocFileChange}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 7,
                  padding: "0.85rem 1rem",
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  backgroundColor: "#f8faf9",
                  cursor:
                    mode === "edit" && !articlePdf ? "pointer" : "default",
                  textAlign: "left",
                  position: "relative",
                }}
                onClick={!articlePdf ? handleDocClick : undefined}
              >
                <FontAwesomeIcon
                  icon={faFilePdf}
                  style={{ fontSize: "1.5rem", color: "#c62828" }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: C.textDk,
                    }}
                  >
                    Product Article
                  </div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: C.textGr,
                      marginTop: 2,
                    }}
                  >
                    {articlePdf ? (
                      <a
                        href={articlePdf.docUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: C.bright, fontWeight: 600 }}
                      >
                        View PDF
                      </a>
                    ) : uploadingDoc ? (
                      "Uploading…"
                    ) : mode === "edit" ? (
                      "Click to upload (PDF)"
                    ) : (
                      "Not uploaded"
                    )}
                  </div>
                </div>
                {articlePdf && mode === "edit" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDoc(articlePdf._id);
                    }}
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: "none",
                      backgroundColor: C.red,
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: "0.55rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
                {articlePdf && mode === "edit" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDocClick();
                    }}
                    style={{
                      marginTop: 4,
                      fontSize: "0.7rem",
                      color: C.mid,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      fontWeight: 600,
                    }}
                  >
                    Replace
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      {alertMsg && (
        <ModalAlert
          size="lg"
          variant={alertVariant}
          message={alertMsg}
          resetCallback={() => setAlertMsg(null)}
        />
      )}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 3000,
            backgroundColor: "rgba(0,0,0,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxUrl}
            alt="Preview"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: "90vw", maxHeight: "90vh",
              borderRadius: 10,
              boxShadow: "0 8px 48px rgba(0,0,0,0.6)",
              objectFit: "contain",
              cursor: "default",
            }}
          />
          <button
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "fixed", top: 18, right: 22,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%",
              width: 38, height: 38,
              color: "#fff", fontSize: "1.1rem",
              cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}

/* ─── Right-side Drawer ───────────────────────────────────────────────────── */
function EditDrawer({ open, mode, type, value, onClose, onSaved, onDeleted }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setText(mode === "edit" ? value?.[type] || "" : "");
      setErrorMsg("");
    }
  }, [open, mode, value, type]);

  const API = process.env.REACT_APP_API_URL;
  const label = type === "product" ? "Product" : "Label";

  const handleSave = () => {
    if (!text.trim()) return;
    setSaving(true);
    const endpoint =
      mode === "add"
        ? `${API}/api/${type === "product" ? "product/addProduct" : "label/addLabel"}`
        : `${API}/api/${type === "product" ? "product/editProduct" : "label/editLabel"}`;
    const payload =
      mode === "edit"
        ? { ...value, [type]: text.trim() }
        : { [type]: text.trim() };
    axios
      .post(endpoint, payload)
      .then((res) => {
        if (res.status === 200) {
          onSaved();
          onClose();
        }
      })
      .catch((err) => {
        const code = err?.response?.data?.code;
        if (code === 11000)
          setErrorMsg(`${label} with same name already exists.`);
        else setErrorMsg("Something went wrong. Please try again.");
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    setDeleting(true);
    const endpoint = `${API}/api/${type === "product" ? "product/deleteProduct" : "label/deleteLabel"}`;
    axios
      .post(endpoint, value)
      .then((res) => {
        if (res.status === 200) {
          onDeleted();
          onClose();
        }
      })
      .catch(() => setErrorMsg("Delete failed. Please try again."))
      .finally(() => setDeleting(false));
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1040,
            backgroundColor: "rgba(0,0,0,0.35)",
          }}
        />
      )}

      {/* Drawer panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 380,
          zIndex: 1050,
          backgroundColor: C.white,
          boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "1rem 1.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: C.mid,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={type === "product" ? faBox : faTag}
                style={{ color: "#fff", fontSize: "0.8rem" }}
              />
            </div>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>
              {mode === "add" ? `Add ${label}` : `Edit ${label}`}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "none",
              borderRadius: 7,
              width: 30,
              height: 30,
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.85rem" }} />
          </button>
        </div>

        {/* Drawer body */}
        <div style={{ flex: 1, padding: "1.5rem 1.25rem", overflowY: "auto" }}>
          <label
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: C.textGr,
              display: "block",
              marginBottom: "0.4rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {label} Name
          </label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            placeholder={`Enter ${label.toLowerCase()} name`}
            style={{
              width: "100%",
              height: 42,
              borderRadius: 9,
              border: `1.5px solid ${C.border}`,
              padding: "0 0.85rem",
              fontSize: "0.9rem",
              outline: "none",
              color: C.textDk,
              boxSizing: "border-box",
            }}
          />
          {errorMsg && (
            <div
              style={{
                marginTop: "0.6rem",
                padding: "0.55rem 0.75rem",
                borderRadius: 8,
                backgroundColor: C.redBg,
                color: C.red,
                fontSize: "0.8rem",
                fontWeight: 500,
              }}
            >
              {errorMsg}
            </div>
          )}
        </div>

        {/* Drawer footer */}
        <div
          style={{
            padding: "1rem 1.25rem",
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleSave}
            disabled={saving || !text.trim()}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 9,
              border: "none",
              backgroundColor: C.mid,
              color: "#fff",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving || !text.trim() ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <FontAwesomeIcon icon={faSave} style={{ fontSize: "0.75rem" }} />
            {saving ? "Saving…" : "Save"}
          </button>
          {mode === "edit" && (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={deleting}
              style={{
                height: 40,
                padding: "0 16px",
                borderRadius: 9,
                border: "none",
                backgroundColor: C.redBg,
                color: C.red,
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.72rem" }} />
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              height: 40,
              padding: "0 16px",
              borderRadius: 9,
              border: `1.5px solid ${C.border}`,
              backgroundColor: C.white,
              color: C.textGr,
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      {showConfirm && (
        <ConfirmBox
          resetCallback={setShowConfirm}
          heading="Confirm Delete"
          message={`Are you sure you want to delete this ${label.toLowerCase()}?`}
          confirmAction={() => {
            setShowConfirm(false);
            handleDelete();
          }}
        />
      )}
      {errorMsg && (
        <ModalAlert
          size="sm"
          variant="danger"
          message={errorMsg}
          resetCallback={() => setErrorMsg("")}
        />
      )}
    </>
  );
}

/* ─── Item row ────────────────────────────────────────────────────────────── */
function ItemRow({ label, description, onEdit, onView, index, thumbUrl }) {
  const [hover, setHover] = useState(false);
  const isView = Boolean(onView);
  const handler = isView ? onView : onEdit;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={isView ? onView : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        padding: "0.7rem 0.85rem",
        backgroundColor: hover
          ? C.light
          : index % 2 === 0
            ? C.white
            : "#f8fbf9",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.12s",
        cursor: isView ? "pointer" : "default",
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 8,
          backgroundColor: C.light,
          border: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={label}
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 3 }}
          />
        ) : (
          <FontAwesomeIcon icon={faImage} style={{ fontSize: "1rem", color: C.border }} />
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: "0.87rem", color: C.textDk, fontWeight: 600 }}>
          {label}
        </div>
        {description && (
          <div
            style={{
              fontSize: "0.76rem",
              color: C.textGr,
              marginTop: 2,
              lineHeight: 1.45,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </div>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handler();
        }}
        style={{
          flexShrink: 0,
          height: 28,
          padding: "0 12px",
          borderRadius: 7,
          border: `1px solid ${C.border}`,
          backgroundColor: hover ? C.white : "transparent",
          color: C.mid,
          fontSize: "0.75rem",
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 5,
          transition: "background 0.12s",
        }}
      >
        <FontAwesomeIcon
          icon={isView ? faEye : faPen}
          style={{ fontSize: "0.62rem" }}
        />
        {isView ? "View" : "Edit"}
      </button>
    </div>
  );
}

/* ─── Product grid card ───────────────────────────────────────────────────── */
function ProductGridCard({ item, onView }) {
  const [hover, setHover] = useState(false);
  const desc = item.shortDescription || "";
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onView}
      style={{
        backgroundColor: C.white,
        borderRadius: 10,
        border: `1.5px solid ${hover ? C.bright : C.border}`,
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: hover
          ? "0 4px 18px rgba(45,90,61,0.12)"
          : "0 1px 4px rgba(20,50,30,0.05)",
        transition: "box-shadow 0.15s, border-color 0.15s",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Image area */}
      <div
        style={{
          height: 140,
          backgroundColor: hover ? "#dff0e6" : C.light,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.15s",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {item.thumbUrl ? (
          <img
            src={item.thumbUrl}
            alt={item.product}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              padding: "8px",
            }}
          />
        ) : (
          <FontAwesomeIcon
            icon={faImage}
            style={{
              fontSize: "3rem",
              color: hover ? C.mid : C.border,
              transition: "color 0.15s",
            }}
          />
        )}
      </div>
      {/* Content */}
      <div
        style={{
          padding: "0.75rem 0.85rem",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: "0.88rem",
            color: C.textDk,
            marginBottom: 5,
            lineHeight: 1.3,
          }}
        >
          {item.product}
        </div>
        <div
          style={{
            fontSize: "0.74rem",
            color: C.textGr,
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {desc}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          style={{
            marginTop: "0.65rem",
            width: "100%",
            height: 30,
            borderRadius: 7,
            border: `1.5px solid ${C.border}`,
            backgroundColor: hover ? C.light : "#f8faf9",
            color: C.mid,
            fontSize: "0.75rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            transition: "background 0.15s",
          }}
        >
          <FontAwesomeIcon icon={faEye} style={{ fontSize: "0.62rem" }} /> View
          Details
        </button>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

/* ─── Pagination bar ──────────────────────────────────────────────────────── */
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= page - delta && i <= page + delta)
    ) {
      pages.push(i);
    }
  }

  const rendered = [];
  let prev = null;
  for (const p of pages) {
    if (prev !== null && p - prev > 1) {
      rendered.push(
        <span
          key={`ellipsis-${p}`}
          style={{ padding: "0 4px", color: C.textGr, fontSize: "0.82rem" }}
        >
          …
        </span>,
      );
    }
    rendered.push(
      <button
        key={p}
        onClick={() => onPage(p)}
        style={{
          minWidth: 32,
          height: 32,
          borderRadius: 7,
          border: "none",
          backgroundColor: p === page ? C.mid : "transparent",
          color: p === page ? "#fff" : C.textDk,
          fontSize: "0.82rem",
          fontWeight: p === page ? 700 : 400,
          cursor: "pointer",
          padding: "0 6px",
          transition: "background 0.12s",
        }}
      >
        {p}
      </button>,
    );
    prev = p;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.65rem 1rem",
        borderTop: `1px solid ${C.border}`,
        backgroundColor: "#f8faf9",
      }}
    >
      <span style={{ fontSize: "0.75rem", color: C.textGr }}>
        Page {page} of {totalPages}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            border: `1px solid ${C.border}`,
            backgroundColor: C.white,
            color: page === 1 ? C.border : C.textDk,
            cursor: page === 1 ? "default" : "pointer",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        {rendered}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            border: `1px solid ${C.border}`,
            backgroundColor: C.white,
            color: page === totalPages ? C.border : C.textDk,
            cursor: page === totalPages ? "default" : "pointer",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────────────────── */
export default function ProductsandLabels() {
  const [products, setProducts] = useState([]);
  const [labels, setLabels] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState("product");

  /* Search per tab */
  const [productSearch, setProductSearch] = useState("");
  const [labelSearch, setLabelSearch] = useState("");

  /* Page per tab */
  const [productPage, setProductPage] = useState(1);
  const [labelPage, setLabelPage] = useState(1);

  /* View mode (grid default, list toggle — products only) */
  const [viewMode, setViewMode] = useState("grid");

  /* Drawer state */
  const [drawer, setDrawer] = useState({
    open: false,
    mode: "add",
    type: "product",
    value: null,
  });
  const [productDrawer, setProductDrawer] = useState({
    open: false,
    product: null,
  });

  const reload = () => {
    setFetching(true);
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/api/product/getProducts`),
      axios.get(`${process.env.REACT_APP_API_URL}/api/label/getLabels`),
    ])
      .then(([p, l]) => {
        if (p.status === 200) setProducts(p.data);
        if (l.status === 200) setLabels(l.data);
      })
      .finally(() => setFetching(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const openAdd = (type) =>
    setDrawer({ open: true, mode: "add", type, value: null });
  const openEdit = (type, item) =>
    setDrawer({ open: true, mode: "edit", type, value: item });
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const search = activeTab === "product" ? productSearch : labelSearch;
  const setSearch = activeTab === "product" ? setProductSearch : setLabelSearch;
  const page = activeTab === "product" ? productPage : labelPage;
  const setPage = activeTab === "product" ? setProductPage : setLabelPage;
  const items = activeTab === "product" ? products : labels;
  const getLabel = (item) =>
    activeTab === "product" ? item.product : item.label;

  const filtered = useMemo(
    () =>
      items.filter((i) =>
        getLabel(i)?.toLowerCase().includes(search.toLowerCase()),
      ),
    [items, search, activeTab], // eslint-disable-line
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  /* Reset to page 1 when search changes */
  useEffect(() => {
    setProductPage(1);
  }, [productSearch]);
  useEffect(() => {
    setLabelPage(1);
  }, [labelSearch]);

  const tabs = [
    { key: "product", label: "Products", icon: faBox, count: products.length },
    { key: "label", label: "Labels", icon: faTag, count: labels.length },
  ];

  return (
    <div
      style={{
        backgroundColor: C.page,
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Page Header ── */}
      <div
        style={{
          backgroundColor: C.white,
          padding: "0.85rem 1.5rem",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${C.border}`,
          boxShadow: "0 1px 4px rgba(20,50,30,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              backgroundColor: C.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faBox}
              style={{ color: C.mid, fontSize: "0.9rem" }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>
              Products &amp; Labels
            </div>
            <div style={{ fontSize: "0.72rem", color: C.textGr }}>
              {products.length} products · {labels.length} labels
            </div>
          </div>
        </div>
        <button
          onClick={reload}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 34,
            padding: "0 12px",
            borderRadius: 8,
            cursor: "pointer",
            border: `1px solid ${C.border}`,
            backgroundColor: C.white,
            color: C.textGr,
            fontSize: "0.8rem",
            fontWeight: 600,
          }}
        >
          <FontAwesomeIcon icon={faSyncAlt} style={{ fontSize: "0.72rem" }} />{" "}
          Refresh
        </button>
      </div>

      {/* ── Tab bar ── */}
      <div
        style={{
          backgroundColor: C.white,
          borderBottom: `2px solid ${C.border}`,
          padding: "0 1.5rem",
          display: "flex",
          gap: 2,
          flexShrink: 0,
        }}
      >
        {tabs.map(({ key, label, icon, count }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0.65rem 1.1rem",
                marginBottom: -2,
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: "0.85rem",
                fontWeight: active ? 700 : 400,
                color: active ? C.mid : C.textGr,
                borderBottom: `2px solid ${active ? C.mid : "transparent"}`,
                transition: "color 0.15s",
              }}
            >
              <FontAwesomeIcon icon={icon} style={{ fontSize: "0.72rem" }} />
              {label}
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  padding: "1px 7px",
                  borderRadius: 10,
                  backgroundColor: active ? C.light : "#f0f0f0",
                  color: active ? C.mid : C.textGr,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, padding: "1.5rem", overflowY: "auto" }}>
        {fetching ? (
          <div
            style={{
              textAlign: "center",
              padding: "4rem",
              color: C.textGr,
              fontSize: "0.9rem",
            }}
          >
            Loading…
          </div>
        ) : (
          <div
            style={{
              backgroundColor: C.white,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              boxShadow: "0 2px 12px rgba(20,50,30,0.05)",
              overflow: "hidden",
            }}
          >
            {/* Toolbar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.85rem 1rem",
                borderBottom: `1px solid ${C.border}`,
                backgroundColor: "#f8faf9",
              }}
            >
              {/* Search */}
              <div style={{ flex: 1, position: "relative" }}>
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: C.textGr,
                    fontSize: "0.75rem",
                  }}
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${activeTab === "product" ? "products" : "labels"}…`}
                  style={{
                    width: "100%",
                    height: 36,
                    borderRadius: 8,
                    border: `1.5px solid ${C.border}`,
                    paddingLeft: 30,
                    paddingRight: 10,
                    fontSize: "0.83rem",
                    outline: "none",
                    color: C.textDk,
                    backgroundColor: C.white,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: "0.78rem",
                  color: C.textGr,
                  whiteSpace: "nowrap",
                }}
              >
                {filtered.length}{" "}
                {activeTab === "product" ? "product" : "label"}
                {filtered.length !== 1 ? "s" : ""}
              </span>
              {/* Grid / List toggle — products only */}
              {activeTab === "product" && (
                <div
                  style={{
                    display: "flex",
                    borderRadius: 8,
                    border: `1.5px solid ${C.border}`,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {[
                    { key: "grid", icon: faTh },
                    { key: "list", icon: faList },
                  ].map(({ key, icon }) => (
                    <button
                      key={key}
                      onClick={() => setViewMode(key)}
                      title={key === "grid" ? "Grid view" : "List view"}
                      style={{
                        width: 34,
                        height: 34,
                        border: "none",
                        cursor: "pointer",
                        backgroundColor: viewMode === key ? C.mid : C.white,
                        color: viewMode === key ? "#fff" : C.textGr,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background 0.12s, color 0.12s",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={icon}
                        style={{ fontSize: "0.72rem" }}
                      />
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => openAdd(activeTab)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  height: 36,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: C.mid,
                  color: "#fff",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.7rem" }} />
                Add {activeTab === "product" ? "Product" : "Label"}
              </button>
            </div>

            {/* List / Grid */}
            {filtered.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "4rem 2rem",
                  color: C.textGr,
                }}
              >
                <FontAwesomeIcon
                  icon={activeTab === "product" ? faBox : faTag}
                  style={{
                    fontSize: "2.5rem",
                    color: C.border,
                    display: "block",
                    marginBottom: "0.75rem",
                  }}
                />
                <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {search
                    ? "No matches found"
                    : `No ${activeTab}s registered yet`}
                </div>
                {!search && (
                  <div style={{ fontSize: "0.82rem", marginTop: 5 }}>
                    Click "Add {activeTab === "product" ? "Product" : "Label"}"
                    to get started
                  </div>
                )}
              </div>
            ) : activeTab === "product" && viewMode === "grid" ? (
              <div
                style={{
                  padding: "1rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))",
                  gap: "0.9rem",
                }}
              >
                {paginated.map((item, idx) => (
                  <ProductGridCard
                    key={item._id || idx}
                    item={item}
                    onView={() =>
                      setProductDrawer({ open: true, product: item })
                    }
                  />
                ))}
              </div>
            ) : (
              paginated.map((item, idx) => (
                <ItemRow
                  key={item._id || idx}
                  index={(safePage - 1) * PAGE_SIZE + idx}
                  label={getLabel(item)}
                  description={
                    activeTab === "product"
                      ? item.shortDescription || undefined
                      : undefined
                  }
                  thumbUrl={activeTab === "product" ? item.thumbUrl : undefined}
                  onView={
                    activeTab === "product"
                      ? () => setProductDrawer({ open: true, product: item })
                      : undefined
                  }
                  onEdit={
                    activeTab === "label"
                      ? () => openEdit(activeTab, item)
                      : undefined
                  }
                />
              ))
            )}

            {/* Pagination */}
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onPage={setPage}
            />
          </div>
        )}
      </div>

      {/* ── Edit/Add Drawer (labels + add product name) ── */}
      <EditDrawer
        open={drawer.open}
        mode={drawer.mode}
        type={drawer.type}
        value={drawer.value}
        onClose={closeDrawer}
        onSaved={reload}
        onDeleted={reload}
      />

      {/* ── Product Detail Drawer ── */}
      <ProductDetailDrawer
        open={productDrawer.open}
        product={productDrawer.product}
        onClose={() => setProductDrawer((d) => ({ ...d, open: false }))}
        onSaved={(updated) => {
          setProducts((prev) =>
            prev.map((p) =>
              p._id === updated._id ? { ...p, product: updated.product } : p,
            ),
          );
          setProductDrawer((d) => ({
            ...d,
            product: { ...d.product, product: updated.product },
          }));
        }}
      />
    </div>
  );
}
